import { Form } from "react-bootstrap";
import { hearAboutUsFieldConfig } from "./configs";

/**
 * HearAboutUsForm Component
 * 
 * Form section for capturing how user heard about the Flipidea platform. Displays dynamic form fields
 * based on the selected "hear about us" option (e.g., referral, social media, search, etc.). Fields are
 * dynamically added/removed from the form state when the selection changes. Supports form validation
 * with error and touched state tracking.
 * 
 * @param {Object} subscription - Subscription/user data object containing hearAboutUs selection
 * @param {Function} setSubscription - Callback to update subscription data
 * @param {Function} handleChange - Form field change handler
 * @param {Object} errors - Form errors object keyed by field name
 * @param {Object} touched - Form touched state object keyed by field name
 * @param {boolean} isLoading - Loading state flag
 * @returns {JSX.Element} Form group with dynamic fields based on selected hear about us option
 */
export default function HearAboutUsForm({ subscription, setSubscription, handleChange, errors = {}, touched = {}, isLoading }) {
  const { hearAboutUs } = subscription;

  const handleHearAboutUsChange = (newOption) => {
    const prevOption = subscription.hearAboutUs;

    // Get dynamic field names for previous selection
    const oldFields = hearAboutUsFieldConfig[prevOption]?.map(field => field.name) || [];

    // Build updated user object without old fields
    const updatedSubscription = { ...subscription, hearAboutUs: newOption };
    oldFields.forEach(field => delete updatedSubscription[field]);

    setSubscription(updatedSubscription);
  };


  const renderField = (field) => {
    const { name, label, type, options, required, placeholder } = field;
    const value = subscription[name] || "";
    const error = errors[name];
    const isTouched = touched[name];

    return (
      <Form.Group className="inputgroup" controlId={name} key={name}>
        <Form.Label>
          {label} {required && <sup className="text-danger">*</sup>}
        </Form.Label>
        {type === "textarea" ? (
          <Form.Control
            as="textarea"
            rows={3}
            name={name}
            className='createaccformbox'
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            disabled={isLoading}
            isInvalid={isTouched && !!error}
          />
        ) : type === "select" ? (
          <Form.Select
            name={name}
            className='createaccformbox'
            value={value}
            onChange={handleChange}
            disabled={isLoading}
            isInvalid={isTouched && !!error}
          >
            <option value="">-- Select --</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            className='createaccinputbox'
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            disabled={isLoading}
            isInvalid={isTouched && !!error}
          />
        )}
        <Form.Control.Feedback type="invalid">
          {error || `Please provide ${label.toLowerCase()}.`}
        </Form.Control.Feedback>
      </Form.Group>
    );
  };

  return (
    <>
      <Form.Group className="inputgroup mt-3" controlId="hearAboutUs">
        <Form.Label>
          How did you hear about us? <sup className="text-danger">*</sup>
        </Form.Label>
        <Form.Select
          name="hearAboutUs"
          value={hearAboutUs}
          onChange={(e) => handleHearAboutUsChange(e.target.value)}
          className='createaccformbox'
          isInvalid={touched.hearAboutUs && !!errors.hearAboutUs}
          required
        >
          <option value="">-- Select an option --</option>
          {Object.keys(hearAboutUsFieldConfig).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {errors.hearAboutUs || "Select an option."}
        </Form.Control.Feedback>
      </Form.Group>

      {hearAboutUs &&
        hearAboutUsFieldConfig[hearAboutUs]?.map((field) => renderField(field))}
    </>
  );
}
